import {
  ComplementaryActivityOrigin,
  ComplementaryActivityStatus,
  IComplementaryActivity,
  IComplementaryActivityHours,
  IComplementaryActivityPayload
} from 'interfaces/complementary_activities';
import { downloadFileFromBlob } from 'utils/donwload-from-blob';
import { getAPI } from 'requests/api';
import { uploadFileToS3 } from 'services/aws-s3';

export const COMPLEMENTARY_ACTIVITY_STATUS_MAP: Record<
  ComplementaryActivityStatus,
  { text: string; color: string }
> = {
  pending: { text: 'Pendente', color: 'warning' },
  approved: { text: 'Aprovada', color: 'success' },
  rejected: { text: 'Rejeitada', color: 'error' }
};

// A API guarda só `location`: atividade online vai com este valor.
export const COMPLEMENTARY_ACTIVITY_ONLINE_LOCATION = 'Online';

export const isOnlineComplementaryActivityLocation = (location?: string) =>
  location?.trim().toLowerCase() ===
  COMPLEMENTARY_ACTIVITY_ONLINE_LOCATION.toLowerCase();

export const COMPLEMENTARY_ACTIVITY_ORIGIN_MAP: Record<
  ComplementaryActivityOrigin,
  string
> = {
  internal: 'Interna',
  external: 'Externa'
};

const basePath = (enrollmentId: string) =>
  `/admin/graduate/courses/enrollments/${enrollmentId}/complementary_activities`;

const statusQuery = (status?: ComplementaryActivityStatus) =>
  status ? `filter[by_status]=${status}` : '';

export const listComplementaryActivities = async (
  enrollmentId: string,
  status?: ComplementaryActivityStatus
): Promise<IComplementaryActivity[]> => {
  const { data } = await getAPI().get<IComplementaryActivity[]>(
    `${basePath(enrollmentId)}?items=100&page=1&${statusQuery(status)}`
  );

  return data;
};

// Mesma conta da área do aluno e do histórico: só AAC aprovada conta como cumprida.
export const getComplementaryActivityHours = async (
  enrollmentId: string
): Promise<IComplementaryActivityHours> => {
  const { data } = await getAPI().get<IComplementaryActivityHours>(
    `${basePath(enrollmentId)}/hours`
  );

  return data;
};

export const createComplementaryActivity = async (
  enrollmentId: string,
  payload: IComplementaryActivityPayload
): Promise<IComplementaryActivity> => {
  const { data } = await getAPI().post<IComplementaryActivity>(
    basePath(enrollmentId),
    payload
  );

  return data;
};

export const updateComplementaryActivity = async (
  enrollmentId: string,
  id: string,
  payload: Partial<IComplementaryActivityPayload>
): Promise<IComplementaryActivity> => {
  const { data } = await getAPI().put<IComplementaryActivity>(
    `${basePath(enrollmentId)}/${id}`,
    payload
  );

  return data;
};

// Só pendente ou rejeitada: a API recusa (422) a exclusão de AAC aprovada.
export const deleteComplementaryActivity = async (
  enrollmentId: string,
  id: string
): Promise<void> => {
  await getAPI().delete(`${basePath(enrollmentId)}/${id}`);
};

export const approveComplementaryActivity = async (
  enrollmentId: string,
  id: string
): Promise<IComplementaryActivity> => {
  const { data } = await getAPI().put<IComplementaryActivity>(
    `${basePath(enrollmentId)}/${id}/approve`
  );

  return data;
};

export const rejectComplementaryActivity = async (
  enrollmentId: string,
  id: string,
  feedback: string
): Promise<IComplementaryActivity> => {
  const { data } = await getAPI().put<IComplementaryActivity>(
    `${basePath(enrollmentId)}/${id}/reject`,
    { feedback }
  );

  return data;
};

// O CSV sai da mesma consulta da lista: o filtro de status aplicado vai junto.
export const downloadComplementaryActivitiesReport = async (
  enrollmentId: string,
  status?: ComplementaryActivityStatus
): Promise<void> => {
  const response = await getAPI().get(
    `${basePath(enrollmentId)}/report?${statusQuery(status)}`,
    { responseType: 'blob' }
  );

  downloadFileFromBlob(new Blob([response.data]), 'aacs.csv');
};

export const uploadComplementaryActivityFile = async (
  enrollmentId: string,
  file: File
): Promise<
  Pick<IComplementaryActivityPayload, 'file_path' | 'file_name' | 'file_size'>
> => {
  const { data } = await getAPI().post<{ path: string; presigned_url: string }>(
    `${basePath(enrollmentId)}/presign_upload`
  );

  await uploadFileToS3(file, data.presigned_url);

  return {
    file_path: data.path,
    file_name: file.name,
    file_size: `${file.size}`
  };
};

export const getComplementaryActivityFileUrl = async (
  enrollmentId: string,
  id: string
): Promise<string> => {
  const { data } = await getAPI().post<{ url: string }>(
    `${basePath(enrollmentId)}/${id}/presign_download`
  );

  return data.url;
};
